import { swSettings } from "app/common/services/swSettings";

import { UNCAPPED_SEGMENT_BUDGET } from "../segmentBudget";

import type { ClassifiedDomain, CustomIndustryAgentRunSnapshot, PersistedCustomIndustryAgentRun } from "./types";
import { IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT } from "./types";

const STORAGE_KEY_PREFIX = "pro:custom-industry-agent-run";

const PERSISTED_STATUSES: readonly PersistedCustomIndustryAgentRun["status"][] = ["generating", "done", "rejected"];

const isPersistedStatus = (status: unknown): status is PersistedCustomIndustryAgentRun["status"] =>
    PERSISTED_STATUSES.some((persisted) => persisted === status);

export const getCustomIndustryAgentRunStorageKey = (): string | null => {
    const userId = swSettings.user?.id;
    if (userId == null) {
        return null;
    }

    return `${STORAGE_KEY_PREFIX}:${userId}`;
};

const asNumber = (value: unknown, fallback: number): number => (typeof value === "number" ? value : fallback);

const asList = <T>(value: unknown): T[] | null => (Array.isArray(value) ? (value as T[]) : null);

const asText = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** `localStorage` is user-writable and outlives every shape this feature has shipped, so an
 *  entry is only usable once the three fields that identify a run are there and the status is
 *  one the UI can render. Anything else counts as no persisted run: recovering a run whose
 *  thread or status is unknown is what left the checklist all-pending under `generating`. */
export const parsePersistedCustomIndustryAgentRun = (raw: string): PersistedCustomIndustryAgentRun | null => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    const candidate = parsed as Partial<PersistedCustomIndustryAgentRun> | null;

    return candidate &&
        typeof candidate.threadId === "string" &&
        candidate.threadId.length > 0 &&
        typeof candidate.countryId === "number" &&
        isPersistedStatus(candidate.status)
        ? (candidate as PersistedCustomIndustryAgentRun)
        : null;
};

const readPersistedRun = (storageKey: string): PersistedCustomIndustryAgentRun | null => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }

        const persisted = parsePersistedCustomIndustryAgentRun(raw);
        if (!persisted) {
            localStorage.removeItem(storageKey);
        }

        return persisted;
    } catch {
        return null;
    }
};

export const loadPersistedCustomIndustryAgentRun = (): PersistedCustomIndustryAgentRun | null => {
    const storageKey = getCustomIndustryAgentRunStorageKey();
    if (!storageKey) {
        return null;
    }

    return readPersistedRun(storageKey);
};

export const snapshotFromPersistedRun = (
    persisted: PersistedCustomIndustryAgentRun,
): CustomIndustryAgentRunSnapshot => ({
    status: persisted.status,
    completedSteps: asNumber(persisted.completedSteps, 0),
    discoveredDomains: asList<ClassifiedDomain>(persisted.discoveredDomains),
    topicSummary: asText(persisted.topicSummary),
    trafficFailures: asList<string>(persisted.trafficFailures),
    createdSegments: asList<ClassifiedDomain>(persisted.createdSegments),
    rulesGenerated: asNumber(persisted.rulesGenerated, 0),
    maxSegments: asNumber(persisted.maxSegments, UNCAPPED_SEGMENT_BUDGET),
    segmentsOverBudget: asNumber(persisted.segmentsOverBudget, 0),
    industry:
        persisted.industryId && persisted.status === "done"
            ? { industryId: persisted.industryId, industryName: persisted.industryName ?? "" }
            : null,
    errorCode: asText(persisted.errorCode),
    description: asText(persisted.description) ?? "",
    countryId: persisted.countryId,
    threadId: persisted.threadId,
    runId: asText(persisted.runId),
    startedAt: typeof persisted.startedAt === "number" ? persisted.startedAt : null,
});

/** Writes the run, or clears the entry when its status is one that is never restored. */
export const persistCustomIndustryAgentRun = (snapshot: CustomIndustryAgentRunSnapshot): void => {
    const storageKey = getCustomIndustryAgentRunStorageKey();
    if (!storageKey) {
        return;
    }

    const { status } = snapshot;
    if (!isPersistedStatus(status)) {
        localStorage.removeItem(storageKey);
        return;
    }

    if (!snapshot.threadId || snapshot.countryId == null) {
        return;
    }

    const persisted: PersistedCustomIndustryAgentRun = {
        threadId: snapshot.threadId,
        runId: snapshot.runId ?? undefined,
        countryId: snapshot.countryId,
        description: snapshot.description,
        status,
        completedSteps: snapshot.completedSteps,
        discoveredDomains: snapshot.discoveredDomains,
        topicSummary: snapshot.topicSummary,
        trafficFailures: snapshot.trafficFailures,
        createdSegments: snapshot.createdSegments,
        rulesGenerated: snapshot.rulesGenerated,
        maxSegments: snapshot.maxSegments,
        segmentsOverBudget: snapshot.segmentsOverBudget,
        industryId: snapshot.industry?.industryId,
        industryName: snapshot.industry?.industryName,
        errorCode: snapshot.errorCode,
        startedAt: snapshot.startedAt ?? undefined,
    };

    localStorage.setItem(storageKey, JSON.stringify(persisted));
};

export const hydrateSnapshotFromStorage = (): CustomIndustryAgentRunSnapshot => {
    const persisted = loadPersistedCustomIndustryAgentRun();

    return persisted ? snapshotFromPersistedRun(persisted) : IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT;
};
